import { isOwnNotificationPreferencePath } from './settings-access-roles';
import type { SupabaseClient } from '@supabase/supabase-js';
import { resolveUserContextForQuery } from '@/lib/supabase/query-context';
import {
  parsePersistedSettingsDelegationForAuthority,
  SETTINGS_DELEGATION_PAGE_KEY,
  SETTINGS_DELEGATION_PREF_TYPE,
} from './settings-delegation';
import {
  parseSettingsVisibilityPreference,
  SETTINGS_VISIBILITY_PAGE_KEY,
} from './settings-visibility';
import {
  isSettingsAdministratorRole,
  resolveSettingsAccess,
  isSettingsControlAllowed,
  SETTINGS_HANDOFF_MESSAGE,
} from './settings-resolver';

/** Session RLS, tenant-filtered, no cache: execution never trusts a rendered decision. */
export async function readSettingsAccessPreferences(sb: SupabaseClient, tenantId: string) {
  const { data, error } = await sb
    .from('layout_preferences')
    .select('page_key, visible_keys, key_order')
    .eq('tenant_id', tenantId)
    .is('user_id', null)
    .eq('pref_type', SETTINGS_DELEGATION_PREF_TYPE)
    .in('page_key', [SETTINGS_DELEGATION_PAGE_KEY, SETTINGS_VISIBILITY_PAGE_KEY]);
  if (error) throw error;
  const rows = data ?? [];
  // Duplicate org preferences are indeterminate, never first-row-wins.
  for (const key of [SETTINGS_DELEGATION_PAGE_KEY, SETTINGS_VISIBILITY_PAGE_KEY]) {
    if (rows.filter((row) => row.page_key === key).length > 1)
      throw new Error('Settings permission is unavailable.');
  }
  const delegationRow = rows.find((row) => row.page_key === SETTINGS_DELEGATION_PAGE_KEY);
  const hiddenRow = rows.find((row) => row.page_key === SETTINGS_VISIBILITY_PAGE_KEY);
  return {
    delegation: parsePersistedSettingsDelegationForAuthority(delegationRow?.visible_keys ?? null),
    hiddenHrefs: parseSettingsVisibilityPreference(
      hiddenRow?.visible_keys as Record<string, unknown> | null,
      hiddenRow?.key_order as string[] | null,
    ).hiddenHrefs,
  };
}

/** Extra application authority boundary; RPC/RLS remains the final resource guard. */
export async function assertSettingsSessionControl(
  sb: SupabaseClient,
  leafId: string,
  controlId: string,
  expectedTenantId?: string,
) {
  try {
    const actor = await resolveUserContextForQuery(sb);
    if (
      !actor?.user ||
      !actor.tenantId ||
      !actor.role ||
      (expectedTenantId && expectedTenantId !== actor.tenantId)
    )
      throw new Error('Access denied.');
    // Administrators are never narrowed, so a malformed row cannot lock them out
    // of the Settings > System repair path.
    const { delegation, hiddenHrefs } =
      isOwnNotificationPreferencePath(leafId) || isSettingsAdministratorRole(actor.role)
        ? { delegation: null, hiddenHrefs: [] }
        : await readSettingsAccessPreferences(sb, actor.tenantId);
    // These controls use the existing authenticated session path, never a
    // privileged executor. Role/scope enforcement still executes in RPC or RLS.
    const access = resolveSettingsAccess({
      leafId,
      actor: { role: actor.role },
      delegation,
      context: {
        hiddenHrefs,
        controlFacts: {
          [controlId]: {
            actionAllowed: true,
            databaseAllowed: true,
            resourceAllowed: true,
          },
        },
      },
    });
    if (!isSettingsControlAllowed(access, controlId)) throw new Error(SETTINGS_HANDOFF_MESSAGE);
  } catch {
    // This check owns no operation work. Authentication and preference failures
    // share the same fixed refusal, including for helpers with a throw contract.
    throw new Error(SETTINGS_HANDOFF_MESSAGE);
  }
}

/** Reviewed Settings keys only. Other application and owner-held payroll keys retain their contract. */
const SETTINGS_LAYOUT_PAGES: Readonly<Record<string, string>> = {
  'settings-approvals': '/settings/approvals',
  'settings-facilities': '/settings/facilities',
  'settings-notifications': '/settings/notifications',
  'settings-users': '/settings/users',
  'settings-users-grid': '/settings/users',
  'settings-retention-policies-grid': '/settings/retention',
  'settings-retention-actions-grid': '/settings/retention',
};

export async function assertSettingsLayoutWrite(
  sb: SupabaseClient,
  tenantId: string,
  pageKey: string,
) {
  const leafId = SETTINGS_LAYOUT_PAGES[pageKey];
  if (leafId) await assertSettingsSessionControl(sb, leafId, 'ownLayoutPreferences', tenantId);
}
